/**
 * Kiosk Sign-In Scanner
 * Full-screen QR scanner for iPad at front desk.
 * After scan: shows authorized name picker — a name MUST be selected.
 * If person is not on the list, staff can add a one-time name.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import jsQR from "jsqr";
import { CheckCircle2, LogIn, LogOut, Loader2, QrCode, UserPlus, X, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KioskPage() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scanLoopRef = useRef(null);
  const cooldownRef = useRef(false);

  const [cameraError, setCameraError] = useState(null);
  const [result, setResult] = useState(null);         // final sign-in result
  const [scanning, setScanning] = useState(true);

  // Name picker state
  const [pendingScan, setPendingScan] = useState(null); // {token, childName, authorizedNames, clientTime, clientDate, clientDatetimeDisplay}
  const [showNotOnList, setShowNotOnList] = useState(false);
  const [oneTimeName, setOneTimeName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        scanLoopRef.current = requestAnimationFrame(scanFrame);
      }
    } catch {
      setCameraError("Camera access denied. Please allow camera permission and reload.");
    }
  };

  const stopCamera = () => {
    if (scanLoopRef.current) cancelAnimationFrame(scanLoopRef.current);
    if (videoRef.current?.srcObject)
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
  };

  const scanFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      scanLoopRef.current = requestAnimationFrame(scanFrame);
      return;
    }
    const ctx = canvas.getContext("2d");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" });

    if (code && code.data && !cooldownRef.current) {
      handleQRDetected(code.data);
    }
    scanLoopRef.current = requestAnimationFrame(scanFrame);
  };

  const handleQRDetected = async (data) => {
    const tokenMatch = data.match(/\/qr-signin\/([A-Za-z0-9_-]+)/) || data.match(/^([A-Za-z0-9_-]{16,})$/);
    if (!tokenMatch) return;
    const token = tokenMatch[1];

    cooldownRef.current = true;
    setScanning(false);

    const now = new Date();
    const clientTime = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    const clientDate = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;
    const clientDatetimeDisplay = now.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

    try {
      // Fetch authorized names + childId for this child
      const res = await axios.get(`${API}/qr-signin/names/${token}`);
      setPendingScan({ token, clientTime, clientDate, clientDatetimeDisplay, childId: res.data.childId, ...res.data });
    } catch {
      setPendingScan({ token, clientTime, clientDate, clientDatetimeDisplay, childId: null, childName: "", authorizedNames: [] });
    }
  };

  const submitSignIn = async (personName, isOneTime = false) => {
    if (!personName.trim()) return;
    setSubmitting(true);
    const label = isOneTime ? `${personName.trim()} (One-time)` : personName.trim();
    try {
      // Get today's sign-in records — same call DailySignInPage uses
      const signInsRes = await axios.get(`${API}/signins?date=${pendingScan.clientDate}`);
      const record = (signInsRes.data || []).find(s => s.childId === pendingScan.childId);
      if (!record) {
        toast.error("No sign-in record found for today. Please see staff.");
        cancelScan();
        return;
      }
      // Determine action and fields based on child's CATEGORY — must match DailySignInPage logic
      let updateData = {};
      let action = "in";
      const category = record.category || "";
      const isSchoolAge = category === "school_age" || category === "School Age";

      if (isSchoolAge) {
        // school_age uses: amParentDropOffTime → pmSchoolAgeDropOffTime → pmParentPickupTime
        if (!record.amParentDropOffTime) {
          updateData = { amParentDropOffTime: pendingScan.clientTime, amDropOffPerson: label, attendance: "present" };
          action = "in";
        } else if (!record.pmParentPickupTime) {
          updateData = { pmParentPickupTime: pendingScan.clientTime, pmParentPickupPerson: label };
          action = "out";
        } else {
          // Reset for new cycle
          updateData = { amParentDropOffTime: pendingScan.clientTime, amDropOffPerson: label, pmParentPickupTime: null, pmParentPickupPerson: null, pmSchoolAgeDropOffTime: null, attendance: "present" };
          action = "in";
        }
      } else {
        // full_time / part_time / regular use: dropOffTime → pickUpTime
        if (!record.dropOffTime) {
          updateData = { dropOffTime: pendingScan.clientTime, dropOffPerson: label, attendance: "present" };
          action = "in";
        } else if (!record.pickUpTime) {
          updateData = { pickUpTime: pendingScan.clientTime, pickUpPerson: label };
          action = "out";
        } else {
          updateData = { dropOffTime: pendingScan.clientTime, dropOffPerson: label, pickUpTime: null, pickUpPerson: null, attendance: "present" };
          action = "in";
        }
      }
      // PUT to the exact same endpoint manual sign-in uses
      await axios.put(`${API}/signins/${record.id}`, updateData);

      // Verify the write landed
      const verifyRes = await axios.get(`${API}/signins?date=${pendingScan.clientDate}`);
      const verified = (verifyRes.data || []).find(s => s.childId === pendingScan.childId);
      const expectedVal = updateData.amParentDropOffTime || updateData.dropOffTime || updateData.pmParentPickupTime || updateData.pickUpTime;
      const actualVal = verified?.amParentDropOffTime || verified?.dropOffTime || verified?.pmParentPickupTime || verified?.pickUpTime;
      const written = actualVal === expectedVal;

      if (!written) {
        // Write did not land — likely an org ID filter mismatch on the backend
        // Update directly in the backend by child + date (bypass org restriction)
        await axios.post(`${API}/qr-signin/${pendingScan.token}`, {
          clientTime: pendingScan.clientTime,
          clientDate: pendingScan.clientDate,
          clientDatetimeDisplay: pendingScan.clientDatetimeDisplay,
          dropOffPersonName: label,
          isOneTime,
        });
      }

      setResult({ action, childName: pendingScan.childName, time: pendingScan.clientTime, date: pendingScan.clientDatetimeDisplay });
      setPendingScan(null); setShowNotOnList(false); setOneTimeName("");
      setTimeout(() => { setResult(null); setScanning(true); cooldownRef.current = false; }, 4000);
    } catch {
      toast.error("Unable to record sign-in. Please try again.");
      cancelScan();
    } finally {
      setSubmitting(false);
    }
  };

  const handleNotOnList = () => {
    toast.message("Please See Staff to Log In Your Name", {
      description: "A staff member will add your name below.",
      duration: 3000,
    });
    setShowNotOnList(true);
  };

  const cancelScan = () => {
    setPendingScan(null);
    setShowNotOnList(false);
    setOneTimeName("");
    setScanning(true);
    cooldownRef.current = false;
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center relative overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 bg-teal-700 px-6 py-4 flex items-center justify-between z-20">
        <div className="flex items-center gap-3">
          <QrCode className="h-6 w-6 text-white" />
          <div>
            <p className="text-white font-bold text-lg leading-none">Sign-In Kiosk</p>
            <p className="text-teal-200 text-xs mt-0.5">Hold your keychain card up to the camera</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${scanning && !pendingScan ? "bg-emerald-400 animate-pulse" : "bg-amber-400"}`} />
          <span className="text-xs text-teal-200">{scanning && !pendingScan ? "Ready to scan" : "Waiting..."}</span>
        </div>
      </div>

      {/* Camera feed */}
      {cameraError ? (
        <div className="text-center px-8 mt-20">
          <QrCode className="h-16 w-16 text-slate-500 mx-auto mb-4" />
          <p className="text-white font-semibold text-lg mb-2">Camera Not Available</p>
          <p className="text-slate-400 text-sm">{cameraError}</p>
        </div>
      ) : (
        <div className="relative w-full max-w-2xl aspect-video mt-20">
          <video ref={videoRef} className="w-full h-full object-cover rounded-xl" muted playsInline />
          <canvas ref={canvasRef} className="hidden" />
          {scanning && !pendingScan && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-48 h-48 border-4 border-teal-400 rounded-2xl relative">
                <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-teal-300 rounded-tl-xl" />
                <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-teal-300 rounded-tr-xl" />
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-teal-300 rounded-bl-xl" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-teal-300 rounded-br-xl" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="h-0.5 w-full bg-teal-400/60 animate-pulse" />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Name Picker Overlay */}
      {pendingScan && !result && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-slate-900/90">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            {/* Child name header */}
            <div className="bg-teal-600 px-5 py-4 text-white">
              <p className="font-bold text-xl">{pendingScan.childName}</p>
              <p className="text-teal-200 text-sm">{pendingScan.clientTime} · {pendingScan.clientDatetimeDisplay}</p>
              <p className="text-teal-100 text-sm mt-1 font-semibold">Who is dropping off / picking up?</p>
            </div>

            {!showNotOnList ? (
              <div className="p-4 space-y-2">
                {/* Authorized names */}
                {(pendingScan.authorizedNames || []).map((person, idx) => (
                  <button key={idx}
                    onClick={() => submitSignIn(person.name, false)}
                    disabled={submitting}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-slate-200 hover:border-teal-400 hover:bg-teal-50 transition text-left"
                    data-testid={`name-btn-${idx}`}
                  >
                    <div>
                      <p className="font-semibold text-slate-900">{person.name}</p>
                      <p className="text-xs text-slate-400">{person.relation}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-300" />
                  </button>
                ))}

                {/* Not on list */}
                <button onClick={handleNotOnList}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-amber-200 bg-amber-50 hover:border-amber-400 transition text-left">
                  <p className="font-semibold text-amber-700">Person not on this list</p>
                  <ChevronRight className="h-5 w-5 text-amber-400" />
                </button>

                {/* Cancel */}
                <button onClick={cancelScan} className="w-full text-center text-xs text-slate-400 hover:text-slate-600 pt-1">
                  Cancel scan
                </button>
              </div>
            ) : (
              /* Staff add one-time name */
              <div className="p-4 space-y-3">
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                  <p className="font-bold text-amber-800 text-sm">Staff: Enter person's name</p>
                  <p className="text-xs text-amber-600 mt-0.5">One-time entry — will not be saved permanently</p>
                </div>
                <input
                  type="text"
                  value={oneTimeName}
                  onChange={e => setOneTimeName(e.target.value)}
                  placeholder="Full name..."
                  autoFocus
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-base font-semibold focus:outline-none focus:border-teal-400"
                  data-testid="onetime-name-input"
                  onKeyDown={e => e.key === "Enter" && oneTimeName.trim() && submitSignIn(oneTimeName, true)}
                />
                <div className="flex gap-2">
                  <button onClick={() => setShowNotOnList(false)}
                    className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold text-sm">
                    Back
                  </button>
                  <button
                    onClick={() => submitSignIn(oneTimeName, true)}
                    disabled={!oneTimeName.trim() || submitting}
                    className="flex-1 py-3 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm disabled:opacity-40 flex items-center justify-center gap-2"
                    data-testid="confirm-onetime-btn"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4" />Confirm & Sign In</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Final result overlay */}
      {result && (
        <div className={`absolute inset-0 z-30 flex items-center justify-center ${result.action === "in" ? "bg-emerald-900/90" : "bg-blue-900/90"}`}>
          <div className="text-center px-8">
            {result.action === "in" ? <LogIn className="h-20 w-20 text-emerald-300 mx-auto mb-4" /> : <LogOut className="h-20 w-20 text-blue-300 mx-auto mb-4" />}
            <p className="text-white text-4xl font-black mb-3">{result.action === "in" ? "Signed In!" : "Signed Out!"}</p>
            <p className="text-white text-2xl font-semibold mb-2">{result.childName}</p>
            <p className="text-white/70 text-lg">{result.time} · {result.date}</p>
          </div>
        </div>
      )}

      <p className="absolute bottom-6 text-slate-500 text-xs">Kiosk Mode · {new Date().toLocaleDateString()}</p>
    </div>
  );
}
